import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static articles matching the reference design
const staticArticles = [
  {
    id: 'article-1',
    title: 'Navigating Working Capital Loans Effectively',
    description: 'Optimize your cash flow with smart financing strategies.',
    image_url: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&q=80',
    created_at: '2024-04-12',
    category: 'loans',
  },
  {
    id: 'article-2',
    title: 'Private Funding vs. Bank Loans: What You Need to Know',
    description: 'Comparing alternative finance to traditional banking.',
    image_url: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&q=80',
    created_at: '2024-03-28',
    category: 'finance',
  },
  {
    id: 'article-3',
    title: 'Capital Markets Opportunities',
    description: 'BiCTh. Math-eafjs contracts',
    image_url: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&q=80',
    created_at: '2024-03-20',
    category: 'finance',
  },
  {
    id: 'article-4',
    title: 'Top Insurance Claims Pitfalls to Avoid',
    description: '',
    image_url: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80',
    created_at: '2024-03-15',
    category: 'insurance',
  },
  {
    id: 'article-5',
    title: 'Unlocking Growth with IPO Strategies',
    description: '',
    image_url: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=600&q=80',
    created_at: '2024-02-20',
    category: 'finance',
  },
  {
    id: 'article-6',
    title: 'Benefits of NBFC Funding for Businesses',
    description: '',
    image_url: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&q=80',
    created_at: '2024-02-05',
    category: 'loans',
  },
  {
    id: 'article-7',
    title: 'Directors & Officers Insurance Explained',
    description: '',
    image_url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&q=80',
    created_at: '2024-01-18',
    category: 'insurance',
  },
];

const InsightsPage = () => {
  const [articles, setArticles] = useState(staticArticles);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState('latest');

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?published_only=true`);
      if (response.data.length > 0) {
        setArticles(response.data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This email is already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div data-testid="insights-page" className="bg-gradient-to-b from-blue-50/30 to-white">
      {/* Hero Section */}
      <section className="py-12 text-center">
        <div className="max-w-6xl mx-auto px-6">
          <h1 
            data-testid="insights-hero-title"
            className="text-3xl md:text-4xl font-semibold text-gray-900 mb-2"
          >
            Insights & Resources
          </h1>
          <p className="text-gray-600">
            Expert Advice for Corporate Success.
          </p>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="featured-article"
            className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-gray-800 to-gray-700"
          >
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12">
                <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3">
                  The Strategic Guide to Corporate Risk Management
                </h2>
                <p className="text-white/70 mb-6">
                  Mastering risk to fortify your business future.
                </p>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2">
                  Read More
                  <span>›</span>
                </button>
              </div>
              <div className="relative h-48 md:h-auto">
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                  alt="Corporate Risk Management"
                  className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg width="120" height="100" viewBox="0 0 120 100" fill="none" className="opacity-80">
                    <rect x="20" y="50" width="20" height="40" rx="2" fill="#4299e1"/>
                    <rect x="50" y="35" width="20" height="55" rx="2" fill="#4299e1"/>
                    <rect x="80" y="20" width="20" height="70" rx="2" fill="#4299e1"/>
                    <path d="M60 30L100 10" stroke="#90cdf4" strokeWidth="2"/>
                    <ellipse cx="60" cy="15" rx="50" ry="20" stroke="#90cdf4" strokeWidth="2" fill="none" opacity="0.3"/>
                  </svg>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('latest')}
                className={`tab-btn ${activeTab === 'latest' ? 'tab-active' : 'tab-inactive'}`}
              >
                Latest Articles
              </button>
              <button
                onClick={() => setActiveTab('popular')}
                className={`tab-btn ${activeTab === 'popular' ? 'tab-active' : 'tab-inactive'}`}
              >
                Popular Topics
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="articles-grid">
            {articles.slice(0, 7).map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                data-testid={`article-card-${index}`}
                className={`article-card ${index >= 5 ? 'md:col-span-1' : ''}`}
              >
                <Link 
                  to={article.id.startsWith('article-') ? '#' : `/insights/${article.id}`}
                  className="block"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm leading-tight">
                      {article.title}
                    </h3>
                    <p className="text-gray-500 text-xs mb-2">
                      {formatDate(article.created_at)}
                    </p>
                    {article.description && (
                      <p className="text-gray-500 text-xs line-clamp-2">
                        {article.description}
                      </p>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-12 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="quote-text text-lg mb-4">
            "Securing funds was smooth and seamless, opening new doors for our business."
          </p>
          <p className="text-gray-500 text-sm">— Retail Chain Owner</p>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-gray-600 mb-6">
            Stay informed with the latest insights and updates.
          </p>
          <form onSubmit={handleSubscribe} className="flex gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-email-input"
              className="newsletter-input flex-1"
            />
            <button
              type="submit"
              disabled={subscribing}
              data-testid="newsletter-subscribe-btn"
              className="cta-primary px-6"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer tagline */}
      <section className="py-8 text-center">
        <p className="text-gray-900 font-medium italic">
          Empower Your <span className="font-semibold">Growth</span> Ambitions.
        </p>
      </section>
    </div>
  );
};

export default InsightsPage;
